import React, { useState, useEffect } from "react";
import { apiFetch } from "../utils/api";
import { toast } from "../components/Toast";
import { useAuth } from "../context/AuthContext";
import { ROLE_DEFAULTS } from "../utils/permissions";

const ALL_PAGES = [
  "Dashboard",
  "Vehicles",
  "Drivers",
  "Routes",
  "Students",
  "Trips",
  "Return Trips Map",
  "Notifications",
  "Users",
  "Reports - Parents",
  "Reports - Trip Report",
  "Reports - Student Report",
];

// ROLE_DEFAULTS imported from utils/permissions

// PermissionsModal: separate component, mounts fresh each open
const PermissionsModal = ({ targetUser, onClose, onSaved }) => {
  const roleDefault = ROLE_DEFAULTS[targetUser.role] ?? ALL_PAGES;

  // Compute initial checked list from saved permissions or role default
  const computeInitial = () => {
    const p = targetUser.permissions;
    if (p) {
      const parsed = typeof p === "string" ? JSON.parse(p) : p;
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
    return roleDefault;
  };

  const [checkedPages, setCheckedPages] = useState(computeInitial);
  const [saving, setSaving] = useState(false);

  const toggle = (page) => {
    setCheckedPages((prev) =>
      prev.includes(page) ? prev.filter((p) => p !== page) : [...prev, page],
    );
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Save null if matches role default (no custom restriction needed)
      const sorted = [...checkedPages].sort();
      const defSorted = [...roleDefault].sort();
      const toSave =
        JSON.stringify(sorted) === JSON.stringify(defSorted) ? null : sorted;

      await apiFetch(`/users/${targetUser.user_id}`, {
        method: "PUT",
        body: JSON.stringify({ permissions: toSave }),
      });
      toast.success(
        "Permissions saved. User must refresh or re-login to see changes.",
      );
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.message || "Failed to save permissions");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2 className="modal-title">
            Edit Permissions — {targetUser.full_name}
          </h2>
          <button className="modal-close" onClick={onClose}>
            &times;
          </button>
        </div>
        <form onSubmit={handleSave}>
          <p
            style={{
              fontSize: "13px",
              color: "var(--text-muted)",
              marginBottom: "16px",
            }}
          >
            Select which pages this user can access. Changes take effect after
            the user refreshes or re-logs in.
          </p>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              marginBottom: "20px",
            }}
          >
            {ALL_PAGES.map((page) => {
              const isChecked = checkedPages.includes(page);
              return (
                <label
                  key={page}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    cursor: "pointer",
                    fontSize: "14px",
                    padding: "8px 12px",
                    borderRadius: "8px",
                    background: isChecked
                      ? "rgba(139,90,43,0.08)"
                      : "transparent",
                    border: `1px solid ${isChecked ? "rgba(139,90,43,0.35)" : "var(--border-color)"}`,
                    transition: "all 0.12s",
                    userSelect: "none",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggle(page)}
                    style={{
                      width: "16px",
                      height: "16px",
                      accentColor: "#8B5E1A",
                      cursor: "pointer",
                    }}
                  />
                  <span>{page}</span>
                </label>
              );
            })}
          </div>

          <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setCheckedPages([...ALL_PAGES])}
            >
              Select All
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setCheckedPages([])}
            >
              Clear All
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setCheckedPages([...roleDefault])}
            >
              Reset to Default
            </button>
          </div>

          <button
            type="submit"
            className="btn-primary btn-block"
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Permissions"}
          </button>
        </form>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
const Users = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [inviteModal, setInviteModal] = useState(false);
  const [manageModal, setManageModal] = useState(false);
  const [permTarget, setPermTarget] = useState(null); // user whose permissions are being edited

  const [inviteForm, setInviteForm] = useState({
    full_name: "",
    email: "",
    phone_number: "",
    role: "admin",
    password: "",
  });
  const [manageForm, setManageForm] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await apiFetch("/users");
      setUsers(res.data || []);
    } catch (e) {
      toast.error("Failed loading users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // ── Invite ──────────────────────────────────
  const handleInviteSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await apiFetch("/users", {
        method: "POST",
        body: JSON.stringify(inviteForm),
      });
      toast.success("User created!");
      setInviteModal(false);
      setInviteForm({
        full_name: "",
        email: "",
        phone_number: "",
        role: "admin",
        password: "",
      });
      loadData();
    } catch (e) {
      toast.error(e.message || "Failed to create user");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Manage (status) ─────────────────────────
  const handleManageOpen = (u) => {
    setManageForm({ ...u, is_active: u.is_active ?? 1 });
    setManageModal(true);
  };

  const handleManageSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await apiFetch(`/users/${manageForm.user_id}`, {
        method: "PUT",
        body: JSON.stringify({ is_active: parseInt(manageForm.is_active, 10) }),
      });
      toast.success("User updated!");
      setManageModal(false);
      loadData();
    } catch (e) {
      toast.error(e.message || "Failed to update user");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Permissions ──────────────────────────────
  const handlePermOpen = (u) => {
    // Pass the user object directly — PermissionsModal reads permissions from it
    setPermTarget(u);
  };

  // ── Helpers ──────────────────────────────────
  const getRoleBadge = (role) => {
    const styles = { admin: { background: "#444", color: "#fff" } };
    return (
      <span
        style={{
          ...(styles[role] || {}),
          padding: "2px 10px",
          borderRadius: "12px",
          fontSize: "11px",
          fontWeight: "600",
          textTransform: "uppercase",
        }}
      >
        {role}
      </span>
    );
  };

  return (
    <div>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
        }}
      >
        <h1 className="page-title text-gradient" style={{ margin: 0 }}>
          System Users
        </h1>
        {user?.role === "admin" && (
          <button
            className="btn-primary"
            onClick={() => setInviteModal(true)}
            style={{ height: "40px" }}
          >
            + Invite User
          </button>
        )}
      </div>

      {/* Table */}
      <div
        className="data-table-container glass-panel"
        style={{ display: "flex", flexDirection: "column" }}
      >
        <table className="data-table" style={{ flex: 1, fontSize: "14px" }}>
          <thead>
            <tr style={{ height: "48px" }}>
              <th style={{ padding: "10px 16px" }}>Full Name</th>
              <th style={{ padding: "10px 16px" }}>Email</th>
              <th style={{ padding: "10px 16px" }}>Role</th>
              <th style={{ padding: "10px 16px" }}>Status</th>
              <th style={{ padding: "10px 16px" }}>Last Login</th>
              <th style={{ padding: "10px 16px" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan="6"
                  style={{ textAlign: "center", padding: "16px" }}
                >
                  Loading...
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td
                  colSpan="6"
                  style={{ textAlign: "center", padding: "16px" }}
                >
                  No users found
                </td>
              </tr>
            ) : (
              users.map((u) => (
                <tr key={u.user_id} style={{ height: "48px" }}>
                  <td style={{ fontWeight: "600", padding: "10px 16px" }}>
                    {u.full_name}
                  </td>
                  <td style={{ padding: "10px 16px" }}>{u.email}</td>
                  <td style={{ padding: "10px 16px" }}>
                    {getRoleBadge(u.role)}
                  </td>
                  <td style={{ padding: "10px 16px" }}>
                    <span
                      className={`badge ${u.is_active === 0 ? "badge-inactive" : "badge-active"}`}
                    >
                      {u.is_active === 0 ? "Inactive" : "Active"}
                    </span>
                  </td>
                  <td style={{ padding: "10px 16px" }}>
                    {u.last_login
                      ? new Date(u.last_login).toLocaleString()
                      : "-"}
                  </td>
                  <td style={{ padding: "10px 16px" }}>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button
                        className="btn-secondary"
                        onClick={() => handleManageOpen(u)}
                      >
                        Manage
                      </button>
                      {/*
                      {user?.role === "admin" &&
                        u.user_id !== user?.user_id && (
                          <button
                            className="btn-primary"
                            onClick={() => handlePermOpen(u)}
                          >
                            Permissions
                          </button>
                        )}
                      */}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Invite Modal */}
      {inviteModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">Invite System User</h2>
              <button
                className="modal-close"
                onClick={() => setInviteModal(false)}
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleInviteSubmit}>
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input
                  type="text"
                  className="form-control"
                  required
                  value={inviteForm.full_name}
                  onChange={(e) =>
                    setInviteForm({ ...inviteForm, full_name: e.target.value })
                  }
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-control"
                    required
                    value={inviteForm.email}
                    onChange={(e) =>
                      setInviteForm({ ...inviteForm, email: e.target.value })
                    }
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input
                    type="text"
                    className="form-control"
                    required
                    value={inviteForm.phone_number}
                    onChange={(e) =>
                      setInviteForm({
                        ...inviteForm,
                        phone_number: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Role</label>
                  <select
                    className="form-control"
                    value={inviteForm.role}
                    onChange={(e) =>
                      setInviteForm({ ...inviteForm, role: e.target.value })
                    }
                  >
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Password</label>
                  <input
                    type="password"
                    className="form-control"
                    required
                    minLength={8}
                    value={inviteForm.password}
                    onChange={(e) =>
                      setInviteForm({ ...inviteForm, password: e.target.value })
                    }
                  />
                </div>
              </div>
              <button
                type="submit"
                className="btn-primary btn-block"
                disabled={submitting}
              >
                {submitting ? "Creating..." : "Create User"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Manage Modal */}
      {manageModal && manageForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">Manage — {manageForm.full_name}</h2>
              <button
                className="modal-close"
                onClick={() => setManageModal(false)}
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleManageSubmit}>
              <div className="form-group">
                <label className="form-label">Account Status</label>
                <select
                  className="form-control"
                  value={manageForm.is_active}
                  onChange={(e) =>
                    setManageForm({ ...manageForm, is_active: e.target.value })
                  }
                >
                  <option value="1">Active</option>
                  <option value="0">Suspended</option>
                </select>
              </div>
              <button
                type="submit"
                className="btn-primary btn-block"
                disabled={submitting}
              >
                {submitting ? "Saving..." : "Save"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Permissions Modal — mounts fresh each time via key */}
      {/*
      {permTarget && (
        <PermissionsModal
          key={permTarget.user_id}
          targetUser={permTarget}
          onClose={() => setPermTarget(null)}
          onSaved={loadData}
        />
      )}
      */}
    </div>
  );
};

export default Users;
